import { Box, Button, Group, Stack, Text } from "@mantine/core"
import dayjs from "dayjs"
import { type PropsWithChildren, type ReactNode, use } from "react"
import { DateContext } from "@/utils"
import { icons } from "../utils"
import classNames from "./Calendar.module.css"
import { CalendarButton } from "./CalendarButton"

interface IProps {
  value: dayjs.Dayjs
  onChange: (value: dayjs.Dayjs) => void
  ical: string
}

export function Calendar({ value, onChange, ical, children }: PropsWithChildren<IProps>) {
  const dtnow = dayjs()

  const startOfCaledar = value.startOf("week")
  const endOfCalendar = value.endOf("month").endOf("week")

  const days = [startOfCaledar.clone()]
  while (days[days.length - 1]!.isBefore(endOfCalendar)) {
    days.push(days[days.length - 1]!.add(1, "day"))
  }
  days.pop() // Remove the last day if it exceeds the end of the month

  return (
    <>
      <Group gap="xs">
        <Button p="xs" color="gray" variant="outline" onClick={() => onChange(value.subtract(1, "month"))}>
          <icons.Left size={16} />
        </Button>
        <Button
          w={160}
          color="gray"
          variant={value.isSame(dtnow, "month") ? "outline" : "filled"}
          onClick={() => onChange(dtnow.startOf("month"))}
        >
          {value.format("MMMM YYYY").toUpperCase()}
        </Button>
        <Button p="xs" color="gray" variant="outline" onClick={() => onChange(value.add(1, "month"))}>
          <icons.Right size={16} />
        </Button>
        <CalendarButton ml="auto" path={ical} />
      </Group>
      <Box className={classNames.calendar}>
        {days.slice(0, 7).map((day) => (
          <Box className={classNames.head} key={day.format("YYYY-MM-DD")}>
            {day.format("dddd").toUpperCase()}
          </Box>
        ))}
        {days.map((day) => (
          <DateContext value={day} key={day.format("YYYY-MM-DD")}>
            {children}
          </DateContext>
        ))}
      </Box>
    </>
  )
}

export const CalendarDay = ({ children, title }: PropsWithChildren<{ title?: ReactNode }>) => {
  const dtnow = dayjs()
  const day = use(DateContext)!

  return (
    <Stack
      data-active={children ? "true" : undefined}
      className={classNames.day + (day.isSame(dtnow, "day") ? ` ${classNames.today}` : "")}
    >
      <Group mb="auto" gap={4}>
        <Text fw="inherit" mr="auto">
          {day.format("D MMM")}
        </Text>
        {title}
      </Group>
      {children}
    </Stack>
  )
}
